import React, { useState, useEffect } from "react"; 
import { FetchUsers } from "../../../hooks/APIHooks";
import { useNavigate } from "react-router-dom";
import { useTable } from "react-table";
import "tailwindcss/tailwind.css";

function AllUsers() {
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const pageSize = 20;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const response = await fetch(FetchUsers, {
          method: "GET", // Changed to GET since no authentication needed
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`Error: ${response.status}`);
        }

        const data = await response.json();
        if (data.error) {
          setError(data.error);
        } else {
          setUsers(data.data || []);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []); // Removed user dependency

  const columns = React.useMemo(
    () => [
      {
        Header: "ID/Name",
        accessor: "name",
        Cell: ({ row }) => (
          <div>
            <div className="flex">
              <p className="font-semibold pr-2">{row.original.user_id}</p>
              <p className="font-semibold">{row.original.name}</p>
            </div>
            <p className="text-sm text-gray-500">{row.original.emailaddress}</p>
          </div>
        ),
        getHeaderProps: () => ({
          style: { width: "80%" },
        }),
      },
      {
        Header: "Phone",
        accessor: "phonenumber",
        Cell: ({ value }) => value || "N/A",
        getHeaderProps: () => ({
          style: { width: "10%" },
        }),
      },
      {
        Header: "Role",
        accessor: "role",
        Cell: ({ value }) => (
          <span className={`px-2 py-1 rounded-full text-xs ${
            value === 'admin' ? 'bg-blue-100 text-blue-800' : 
            value === 'user' ? 'bg-green-100 text-green-800' : 
            'bg-gray-100 text-gray-800'
          }`}>
            {value || 'user'}
          </span>
        ),
        getHeaderProps: () => ({
          style: { width: "10%" },
        }),
      },
    ],
    []
  );

  // Enhanced search - search by name, email, or phone number
  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.emailaddress?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.phonenumber?.includes(searchTerm)
  );

  const paginatedData = React.useMemo(() => {
    const start = currentPage * pageSize;
    const end = start + pageSize;
    return filteredUsers.slice(start, end);
  }, [filteredUsers, currentPage]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data: paginatedData });

  const handleUserClick = (userId) => {
    navigate(`/auth/users/user-details/${userId}`);
  };

  return (
    <div className="p-4">
      <h1 className="text-xl font-bold mb-4">All Users</h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {loading ? (
        <div className="flex justify-center items-center py-8">
          <p className="text-gray-600">Loading users...</p>
        </div>
      ) : (
        <>
          {/* Search Input */}
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search by name, email, or phone number"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(0); // Reset to first page when searching
              }}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Users Table */}
          <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
            <table
              {...getTableProps()}
              className="min-w-full bg-white"
            >
              <thead className="bg-gray-50">
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        {...column.getHeaderProps()}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b"
                      >
                        {column.render("Header")}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()} className="divide-y divide-gray-200">
                {rows.map((row) => {
                  prepareRow(row);
                  return (
                    <tr
                      {...row.getRowProps()}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => handleUserClick(row.original.user_id)}
                    >
                      {row.cells.map((cell) => (
                        <td
                          {...cell.getCellProps()}
                          className="px-4 py-3 text-sm text-gray-900 truncate"
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            {filteredUsers.length === 0 && !loading && (
              <div className="text-center py-8">
                <p className="text-gray-500">No users found.</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {filteredUsers.length > 0 && (
            <div className="flex items-center justify-between mt-4 px-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                disabled={currentPage === 0}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  currentPage === 0
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                Previous
              </button>
              
              <span className="text-sm text-gray-700">
                Page {currentPage + 1} of {Math.ceil(filteredUsers.length / pageSize)}
                <span className="text-gray-500 ml-2">
                  ({filteredUsers.length} users)
                </span>
              </span>
              
              <button
                onClick={() =>
                  setCurrentPage((prev) =>
                    Math.min(prev + 1, Math.ceil(filteredUsers.length / pageSize) - 1)
                  )
                }
                disabled={currentPage === Math.ceil(filteredUsers.length / pageSize) - 1}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  currentPage === Math.ceil(filteredUsers.length / pageSize) - 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AllUsers;